import { useCallback, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import { NumericInput } from '@/components/shared/NumericInput';

/* ── Column Config ── */
export interface DetailColumn<T> {
    key: keyof T & string;
    label: ReactNode;
    width?: string;       // e.g. "w-24"
    type?: 'text' | 'number';
    decimals?: number;
    placeholder?: string;
    required?: boolean;
    readOnly?: boolean;
    align?: 'left' | 'right' | 'center';
    render?: (value: any, row: T, index: number, updateRow: (updates: Partial<T>) => void, hasError?: boolean) => ReactNode;
}

/* ── Props ── */
export interface InlineDetailTableProps<T extends Record<string, any>> {
    columns: DetailColumn<T>[];
    rows: T[];
    onChange: (rows: T[]) => void;
    createEmptyRow?: () => T;
    title?: string;
    icon?: ReactNode;
    minRows?: number;
    maxRows?: number;
    footer?: (rows: T[]) => ReactNode;
    rowErrors?: Record<string, boolean>[];
    onAddRequest?: () => void;
    /** Keep the row number and first data column visible while scrolling wide tables horizontally */
    stickyFirstColumn?: boolean;
}

// Sticky from md up only: on phones the pinned columns would cover the whole viewport
const STICKY_NUM = 'md:sticky md:left-0 md:z-10 bg-white group-hover:bg-slate-50';
const STICKY_FIRST = 'md:sticky md:left-8 md:z-10 bg-white group-hover:bg-slate-50 md:shadow-[4px_0_6px_-4px_rgba(15,23,42,0.15)]';

/* ── Component ── */
export default function InlineDetailTable<T extends Record<string, any>>({
    columns,
    rows,
    onChange,
    createEmptyRow,
    title,
    icon,
    minRows = 0,
    maxRows = 50,
    footer,
    rowErrors,
    onAddRequest,
    stickyFirstColumn = false,
}: InlineDetailTableProps<T>) {
    const handleAdd = useCallback(() => {
        if (onAddRequest) {
            onAddRequest();
            return;
        }
        if (createEmptyRow && rows.length < maxRows) {
            onChange([...rows, createEmptyRow()]);
        }
    }, [rows, onChange, createEmptyRow, maxRows, onAddRequest]);

    const handleRemove = useCallback((index: number) => {
        if (rows.length <= minRows) return;
        onChange(rows.filter((_, i) => i !== index));
    }, [rows, onChange, minRows]);

    const handleCellChange = useCallback((index: number, key: string, value: any) => {
        const updated = rows.map((row, i) =>
            i === index ? { ...row, [key]: value } as T : row,
        );
        onChange(updated);
    }, [rows, onChange]);

    const handleRowUpdate = useCallback((index: number, updates: Partial<T>) => {
        const updated = rows.map((row, i) =>
            i === index ? { ...row, ...updates } as T : row,
        );
        onChange(updated);
    }, [rows, onChange]);

    return (
        <div className="space-y-3">
            {/* Header */}
            {title && (
                <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        {icon}
                        {title}
                        <span className="text-xs font-normal text-slate-400">({rows.length} dòng)</span>
                    </h4>
                </div>
            )}

            {/* Table */}
            <div className="border rounded-md overflow-auto">
                <table className="apv-inline-edit-table w-full min-w-max text-sm text-left">
                    <thead className="bg-slate-50 text-slate-500 font-medium text-[11px] uppercase">
                        <tr>
                            <th className={`px-1 py-1.5 w-8 min-w-8 max-w-8 text-center ${stickyFirstColumn ? 'md:sticky md:left-0 md:z-10 bg-slate-50' : ''}`}>#</th>
                            {columns.map((col, ci) => (
                                <th
                                    key={col.key}
                                    className={`px-1 py-1.5 whitespace-nowrap ${col.width || ''} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${stickyFirstColumn && ci === 0 ? 'md:sticky md:left-8 md:z-10 bg-slate-50 md:shadow-[4px_0_6px_-4px_rgba(15,23,42,0.15)]' : ''}`}
                                >
                                    {col.label}
                                    {col.required && <span className="text-destructive ml-0.5">*</span>}
                                </th>
                            ))}
                            <th className="px-0.5 py-1.5 w-7"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length + 2} className="px-4 py-8 text-center text-slate-400 text-sm">
                                    Chưa có sản phẩm. Nhấn "Thêm dòng" để bắt đầu.
                                </td>
                            </tr>
                        )}
                        {rows.map((row, i) => (
                            <tr key={i} className="hover:bg-slate-50/50 group">
                                <td className={`px-1 py-1 w-8 min-w-8 max-w-8 text-center text-slate-400 text-xs align-top pt-2.5 ${stickyFirstColumn ? STICKY_NUM : ''}`}>{i + 1}</td>
                                {columns.map((col, ci) => {
                                    const hasError = rowErrors?.[i]?.[col.key];
                                    return (
                                    <td key={col.key} className={`px-0.5 py-1 align-top ${col.align === 'right' ? 'text-right' : ''} ${stickyFirstColumn && ci === 0 ? STICKY_FIRST : ''}`}>
                                        {col.render ? (
                                            col.render(row[col.key], row, i, (updates) => handleRowUpdate(i, updates), hasError)
                                        ) : col.readOnly ? (
                                            <span className="px-1 py-1.5 block text-sm font-mono">
                                                {row[col.key] ?? '—'}
                                            </span>
                                        ) : col.type === 'number' ? (
                                            <NumericInput
                                                size={1}
                                                value={Number(row[col.key]) || 0}
                                                onChange={(val) => handleCellChange(i, col.key, val)}
                                                decimals={col.decimals ?? 0}
                                                placeholder={col.placeholder || '0'}
                                                className={`h-8 text-sm ${hasError ? 'border-red-500 focus-visible:ring-red-500 bg-red-50' : ''}`}
                                            />
                                        ) : (
                                            <Input
                                                size={1}
                                                type={col.type || 'text'}
                                                value={row[col.key] ?? ''}
                                                placeholder={col.placeholder}
                                                className={`h-8 text-sm border-slate-200 focus:border-indigo-400 ${hasError ? 'border-red-500 focus-visible:ring-red-500 bg-red-50' : ''}`}
                                                onChange={(e) => handleCellChange(i, col.key, e.target.value)}
                                            />
                                        )}
                                    </td>
                                )})}
                                <td className="px-0.5 py-1 text-center align-top w-7">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition"
                                        onClick={() => handleRemove(i)}
                                        disabled={rows.length <= minRows}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {/* Add row button — between items and totals */}
                    <tbody>
                        <tr>
                            <td className="px-2 py-1"></td>
                            <td className="px-1 py-1" colSpan={2}>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleAdd}
                                    disabled={rows.length >= maxRows}
                                    className="apv-accent-text apv-accent-bg-hover hover:bg-slate-100 border border-slate-200/80 h-7 text-xs px-2.5 font-semibold rounded-md flex items-center gap-1"
                                >
                                    <Plus className="h-3.5 w-3.5" /> Thêm dòng
                                </Button>
                            </td>
                            <td colSpan={columns.length - 1} className="px-2 py-1"></td>
                        </tr>
                    </tbody>
                    {footer && rows.length > 0 && (
                        <tfoot className="bg-slate-50 font-bold text-sm">
                            {footer(rows)}
                        </tfoot>
                    )}
                </table>
            </div>


        </div>
    );
}
